import { useEffect, useMemo, useRef, useState } from "react";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Bold, Braces, Italic, List, ListOrdered, Redo2, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { normalizeString } from "@/utils/searchUtils";
import { VariavelNode } from "@/components/finance/editor/VariavelNode";

export interface VariavelDoCatalogo {
  nome: string;
  rotulo: string;
  exemplo?: string | null;
}

interface EditorDeTextoRicoProps {
  value: string;
  onChange: (html: string) => void;
  ariaLabel: string;
  disabled?: boolean;
  variaveis?: VariavelDoCatalogo[];
  aparencia?: "caixa" | "folha";
  className?: string;
}

const ESTILO_DO_CONTEUDO = cn(
  "text-sm leading-relaxed focus:outline-hidden",
  "[&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0",
  "[&_h3]:mt-3 [&_h3]:mb-1.5 [&_h3]:text-base [&_h3]:font-semibold",
  "[&_h4]:mt-3 [&_h4]:mb-1.5 [&_h4]:text-sm [&_h4]:font-semibold",
  "[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5",
  "[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5",
  "[&_li]:my-0.5",
  "[&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-muted-foreground/30 [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground",
  "[&_a]:text-primary [&_a]:underline",
);

/**
 * Editor rich text restrito às tags que o backend aceita (p, br, strong, em,
 * u, s, ul, ol, li, h3, h4, blockquote, a) mais os campos do documento, que
 * viram chips (`<span data-variavel="NOME"></span>` no HTML salvo).
 */
export function EditorDeTextoRico({
  value,
  onChange,
  ariaLabel,
  disabled,
  variaveis,
  aparencia = "caixa",
  className,
}: EditorDeTextoRicoProps) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // A barra da folha só aparece com o foco no texto; a busca de campos tira o
  // foco dele, e sem a barra o popover perde a âncora e abre no canto da tela.
  const [buscandoCampo, setBuscandoCampo] = useState(false);

  const assinaturaDasVariaveis = (variaveis ?? []).map((variavel) => `${variavel.nome}:${variavel.rotulo}`).join("|");
  const rotulos = useMemo(
    () => Object.fromEntries((variaveis ?? []).map((variavel) => [variavel.nome, variavel.rotulo])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assinaturaDasVariaveis],
  );

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          heading: { levels: [3, 4] },
          code: false,
          codeBlock: false,
          horizontalRule: false,
        }),
        VariavelNode.configure({ rotulos }),
      ],
      content: value,
      editable: !disabled,
      editorProps: {
        attributes: {
          "aria-label": ariaLabel,
          class: cn(ESTILO_DO_CONTEUDO, aparencia === "caixa" ? "min-h-[120px] px-3 py-2" : "min-h-6 px-1 py-0.5"),
        },
      },
      onUpdate: ({ editor: editorAtualizado }) => {
        onChangeRef.current(editorAtualizado.getHTML());
      },
    },
    [rotulos],
  );

  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  useEffect(() => {
    if (!editor) return;
    // Sem emitir update: o TipTap trata setEditable como edição, e isso
    // salvava o texto normalizado ao abrir a tela, sem ninguém ter mexido.
    editor.setEditable(!disabled, false);
  }, [disabled, editor]);

  if (aparencia === "folha") {
    return (
      <div className={cn("group/texto rounded-md transition-colors focus-within:bg-sky-50/40", className)}>
        <div
          className={cn(
            "sticky top-0 z-10 hidden rounded-md border bg-background shadow-xs group-focus-within/texto:inline-flex",
            buscandoCampo && "inline-flex",
            disabled && "hidden!",
          )}
        >
          <BarraDeFerramentasTexto
            editor={editor}
            disabled={disabled}
            variaveis={variaveis}
            onBuscaDeCampoChange={setBuscandoCampo}
          />
        </div>
        <EditorContent editor={editor} />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "rounded-md border border-input bg-background ring-offset-background",
        "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
        disabled && "opacity-50",
        className,
      )}
    >
      <BarraDeFerramentasTexto editor={editor} disabled={disabled} variaveis={variaveis} />
      <Separator />
      <EditorContent editor={editor} />
    </div>
  );
}

interface BarraDeFerramentasTextoProps {
  editor: Editor | null;
  disabled?: boolean;
  variaveis?: VariavelDoCatalogo[];
  onBuscaDeCampoChange?: (aberta: boolean) => void;
}

export function BarraDeFerramentasTexto({
  editor,
  disabled,
  variaveis = [],
  onBuscaDeCampoChange,
}: BarraDeFerramentasTextoProps) {
  const botaoAtivo = (ativo: boolean) => cn("h-7 w-7", ativo && "bg-accent text-accent-foreground");
  const semEditor = disabled || !editor;

  return (
    <div className="flex flex-wrap items-center gap-0.5 p-1">
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className={botaoAtivo(!!editor?.isActive("bold"))}
        disabled={semEditor}
        onClick={() => editor?.chain().focus().toggleBold().run()}
        title="Negrito"
      >
        <Bold className="h-3.5 w-3.5" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className={botaoAtivo(!!editor?.isActive("italic"))}
        disabled={semEditor}
        onClick={() => editor?.chain().focus().toggleItalic().run()}
        title="Itálico"
      >
        <Italic className="h-3.5 w-3.5" />
      </Button>
      <Separator orientation="vertical" className="mx-1 h-5" />
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className={botaoAtivo(!!editor?.isActive("bulletList"))}
        disabled={semEditor}
        onClick={() => editor?.chain().focus().toggleBulletList().run()}
        title="Lista com marcadores"
      >
        <List className="h-3.5 w-3.5" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className={botaoAtivo(!!editor?.isActive("orderedList"))}
        disabled={semEditor}
        onClick={() => editor?.chain().focus().toggleOrderedList().run()}
        title="Lista numerada"
      >
        <ListOrdered className="h-3.5 w-3.5" />
      </Button>
      <Separator orientation="vertical" className="mx-1 h-5" />
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-7 w-7"
        disabled={semEditor || !editor?.can().undo()}
        onClick={() => editor?.chain().focus().undo().run()}
        title="Desfazer"
      >
        <Undo2 className="h-3.5 w-3.5" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-7 w-7"
        disabled={semEditor || !editor?.can().redo()}
        onClick={() => editor?.chain().focus().redo().run()}
        title="Refazer"
      >
        <Redo2 className="h-3.5 w-3.5" />
      </Button>
      {variaveis.length > 0 && (
        <>
          <Separator orientation="vertical" className="mx-1 h-5" />
          <InserirCampo
            editor={editor}
            disabled={semEditor}
            variaveis={variaveis}
            onOpenChange={onBuscaDeCampoChange}
          />
        </>
      )}
    </div>
  );
}

interface InserirCampoProps {
  editor: Editor | null;
  disabled: boolean;
  variaveis: VariavelDoCatalogo[];
  onOpenChange?: (aberto: boolean) => void;
}

function InserirCampo({ editor, disabled, variaveis, onOpenChange }: InserirCampoProps) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState("");
  const encontradas = useMemo(() => {
    const termo = normalizeString(busca);
    if (!termo) return variaveis;
    return variaveis.filter((variavel) =>
      [variavel.rotulo, variavel.nome, variavel.exemplo].some((texto) => normalizeString(texto).includes(termo)),
    );
  }, [busca, variaveis]);

  // Ao fechar, o foco volta ao texto antes de a barra soltar: sem ele a barra
  // some durante a animação de saída e o popover pula para o canto da tela.
  const mudarAberto = (proximo: boolean) => {
    setAberto(proximo);
    if (!proximo) {
      setBusca("");
      editor?.commands.focus();
    }
    onOpenChange?.(proximo);
  };

  const inserir = (nome: string) => {
    editor?.chain().focus().inserirVariavel(nome).run();
    mudarAberto(false);
  };

  return (
    <Popover open={aberto} onOpenChange={mudarAberto}>
      <PopoverTrigger asChild>
        <Button type="button" size="sm" variant="ghost" className="h-7 gap-1 px-2 text-xs" disabled={disabled}>
          <Braces className="h-3.5 w-3.5" />
          Inserir campo
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0" onCloseAutoFocus={(event) => event.preventDefault()}>
        <Command shouldFilter={false}>
          <CommandInput placeholder="Buscar campo..." value={busca} onValueChange={setBusca} />
          <CommandList className="max-h-80">
            <CommandEmpty>Nenhum campo encontrado.</CommandEmpty>
            <CommandGroup heading="Preenchido com os dados de cada documento">
              {encontradas.map((variavel) => (
                <CommandItem
                  key={variavel.nome}
                  value={variavel.nome}
                  onSelect={() => inserir(variavel.nome)}
                  className="flex flex-col items-start gap-0"
                >
                  <span className="text-sm">{variavel.rotulo}</span>
                  {variavel.exemplo && (
                    <span className="text-[11px] text-muted-foreground">Ex.: {variavel.exemplo}</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
